import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { FileQuestion } from '@gravity-ui/icons';
import { Button, Icon, Popup, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { devicesApi } from 'client/api';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { Field } from 'client/blocks/form/Field/Field';
import { PageLayout } from 'client/blocks/Layout/PageLayout';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { wrapInText } from 'client/lib/i18n-utils';
import { MarketDevice } from 'client/model/devices';
import { SmartHome } from 'client/model/skill';
import * as keyset from 'i18n/Settings';
import * as smartHomeKeyset from 'i18n/SmartHomeForm';

import styles from './DeviceLinking.module.scss';
import { DeviceLinkingAlreadyConnectedList } from './DeviceLinkingAlreadyConnectedList';
import { DeviceLinkingInput } from './DeviceLinkingInput';

interface Props {
    isDraft: boolean;
    isEditable: boolean;
    aliceApp: SmartHome;
}

interface PicturerHintProps {
    src: string;
}

const DEVICE_LINKING_TOAST = 'DEVICE_LINKING_TOAST';

const t = i18n(keyset);
const tSmartHome = i18n(smartHomeKeyset);

const marketIdPicture = `${__appConfig.app.staticResourcesUrl}/dialogs/dev-console/images/market-id.webp`;

const PictureHint: FC<PicturerHintProps> = ({ src }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popupRef = useRef(null);

    return (
        <>
            <Button
                className={styles.hint__button}
                onClick={() => setIsOpen((state) => !state)}
                onBlur={() => setIsOpen(false)}
                ref={popupRef}
                size="xs"
                view="flat"
            >
                <Icon data={FileQuestion} />
            </Button>
            <Popup anchorRef={popupRef} open={isOpen} placement={['bottom-end', 'top-end']}>
                <img className={styles.hint__picture} src={src} />
            </Popup>
        </>
    );
};

export const DeviceLinking: FC<Props> = ({ aliceApp, isDraft, isEditable }) => {
    const [devices, setDevices] = useState<MarketDevice[]>([]);
    const { add: addToast } = useToaster();

    const [fetchDevices, state] = useFetchingHandler({
        handler: () => devicesApi.getDeviceList(aliceApp.id, aliceApp.channel, isDraft),
        onLoad: (devices) => setDevices(devices),
        onError: () =>
            addToast({ title: tSmartHome('cloud-error-reload'), theme: 'danger', name: DEVICE_LINKING_TOAST }),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void fetchDevices(), [aliceApp.id, isDraft]);

    const onDeviceAdd = useCallback((device: MarketDevice) => {
        setDevices((state) => [device, ...state]);
    }, []);

    const afterDeviceRemove = useCallback((deviceId: string) => {
        setDevices((state) => state.filter((device) => device.marketId !== deviceId));
    }, []);

    const disabled = !isEditable && !__appConfig.user.isAdmin;

    return (
        <ErrorBoundary withHomepageLink>
            <PageLayout aliceApp={aliceApp} pageTitle={t('submenu-devices')}>
                <Field
                    fieldTitle={t('devices-supported')}
                    fieldDescription={wrapInText((_) => <PictureHint src={marketIdPicture} />)(
                        t('devices-from-market'),
                    )}
                >
                    <DeviceLinkingInput
                        aliceApp={aliceApp}
                        isDraft={isDraft}
                        onDeviceAdd={onDeviceAdd}
                        disabled={disabled}
                        devices={devices}
                    />
                    {state === 'loading' ? (
                        <PageLoader />
                    ) : (
                        <DeviceLinkingAlreadyConnectedList
                            aliceApp={aliceApp}
                            isDraft={isDraft}
                            devices={devices}
                            afterDeviceRemove={afterDeviceRemove}
                            disabled={disabled}
                        />
                    )}
                </Field>
            </PageLayout>
        </ErrorBoundary>
    );
};
